import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
} from '../../components/PageConfigProvider/constants';
import type { TabContent } from '../../components/PageConfigProvider/page-reducer';
import {
  type Page,
  PageType,
  RevelioPageId,
} from '../../components/PageConfigProvider/types';
import type { MetricValues } from '../../components/PageConfigProvider/types/metric';
import { FilterId } from '../../filters/types/filters';

const companySentimentMetrics: MetricValues[] = [
  'overall',
  'business_outlook',
  'career_growth',
  'compensation',
  'benefits',
  'healthcare',
  'retirement_pension',
  'childcare',
  'education_assistance',
  'wellness_programs',
  'work_life_balance',
  'diversity_and_inclusion',
  'leadership',
  'culture',
  'remote_work',
  'onboarding',
  'mentorship',
  'technology',
  'promotion_opportunities',
  'paid_time_off',
  'transparency',
  'efficiency_and_bureaucracy',
  'product_and_service_quality',
  'prestige',
  'coworkers',
  'mission_and_vision',
  'workplace_facilities',
  'training',
  'clients_and_customers',
  'company_growth',
  'schedule_flexibility',
  'job_security',
  'overtime',
  'family_support',
  'commission',
  'independence',
  'commute',
  'safety',
  'internal_communication',
  'office_politics',
  'harassment',
  'honesty',
  'ethics',
  'commitment',
  'compliance',
  'integrity',
];

const sentimentInfoText =
  'Sentiment scores are derived from both the free text and the star ratings associated with employee reviews, and normalized so that 50% is the median score amongst peer companies. Sample size includes all reviews with star ratings predictive of a specific sentiment topic, as well as all reviews that contain any signal for that sentiment topic in their free text, within the previous 12 months.';

const sentimentBarAxisRange = {
  maxValue: 1,
  minValue: 0,
};

export const companySentimentSnapshotItems: TabContent['items'] = [
  {
    position: { x: 0, width: 11, y: 0, height: 12 },
    id: 'company-sentiment-snapshot-overall',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'bar',
      defaultMetricOptions: companySentimentMetrics,
      ...sentimentBarAxisRange,
    },
  },
  {
    position: { x: 11, width: 11, y: 0, height: 12 },
    id: 'company-sentiment-snapshot-business-outlook',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'bar',
      defaultMetricOptions: companySentimentMetrics,
      ...sentimentBarAxisRange,
    },
  },
  {
    position: { x: 22, width: 11, y: 0, height: 12 },
    id: 'company-sentiment-snapshot-career-growth',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'bar',
      defaultMetricOptions: companySentimentMetrics,
      ...sentimentBarAxisRange,
    },
  },
  {
    position: { x: 0, width: 11, y: 12, height: 12 },
    id: 'company-sentiment-snapshot-compensation',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'bar',
      defaultMetricOptions: companySentimentMetrics,
      ...sentimentBarAxisRange,
    },
  },
  {
    position: { x: 11, width: 11, y: 12, height: 12 },
    id: 'company-sentiment-snapshot-benefits',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'bar',
      defaultMetricOptions: companySentimentMetrics,
      ...sentimentBarAxisRange,
    },
  },
  {
    position: { x: 22, width: 11, y: 12, height: 12 },
    id: 'company-sentiment-snapshot-culture',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'bar',
      defaultMetricOptions: companySentimentMetrics,
      ...sentimentBarAxisRange,
    },
  },
  {
    position: { x: 0, width: 11, y: 24, height: 12 },
    id: 'company-sentiment-snapshot-work-life-balance',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'bar',
      defaultMetricOptions: companySentimentMetrics,
      ...sentimentBarAxisRange,
    },
  },
  {
    position: { x: 11, width: 11, y: 24, height: 12 },
    id: 'company-sentiment-snapshot-diversity-and-inclusion',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'bar',
      defaultMetricOptions: companySentimentMetrics,
      ...sentimentBarAxisRange,
    },
  },
  {
    position: { x: 22, width: 11, y: 24, height: 12 },
    id: 'company-sentiment-snapshot-leadership',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'bar',
      defaultMetricOptions: companySentimentMetrics,
      ...sentimentBarAxisRange,
    },
  },
  {
    position: { x: 33, width: 15, y: 0, height: 18 },
    id: 'company-sentiment-snapshot-positive-reviews',
    title: 'Positive Reviews',
    info: {
      text: 'An AI-generated summary of all positive reviews.',
    },
    plot: {
      defaultMetricOptions: ['positive_review'],
      primaryEntitySwitcher: true,
      chartType: 'sentiment_review',
    },
  },
  {
    position: { x: 33, width: 15, y: 18, height: 18 },
    id: 'company-sentiment-snapshot-negative-reviews',
    title: 'Negative Reviews',
    info: {
      text: 'An AI-generated summary of all negative reviews.',
    },
    plot: {
      defaultMetricOptions: ['negative_review'],
      primaryEntitySwitcher: true,
      chartType: 'sentiment_review',
    },
  },
];

export const companySentimentSingleSnapshotItems: TabContent['items'] = [
  {
    position: { x: 0, width: 33, y: 0, height: 36 },
    id: 'company-sentiment-snapshot-sentiment-topics',
    title: 'Sentiment Topics',
    info: {
      text: 'Sentiment scores are derived from both the free text and the star ratings associated with employee reviews, and normalized so that 50% is the median score amongst peer companies. Sample size includes all reviews with star ratings predictive of a specific sentiment topic, as well as all reviews that contain any signal for that sentiment topic in their free text, within the previous 12 months.',
    },
    plot: {
      defaultMetricOptions: ['sentiment_top_bottom'],
      chartType: 'diverging-bar',
      divergingBar: {
        negativeLabel: 'Factors of Attrition',
        positiveLabel: 'Factors of Hiring',
      },
    },
  },
  {
    position: { x: 33, width: 15, y: 0, height: 18 },
    id: 'company-sentiment-snapshot-positive-reviews',
    title: 'Positive Reviews',
    info: {
      text: 'An AI-generated summary of all positive reviews.',
    },
    plot: {
      defaultMetricOptions: ['positive_review'],
      primaryEntitySwitcher: true,
      chartType: 'sentiment_review',
    },
  },
  {
    position: { x: 33, width: 15, y: 18, height: 18 },
    id: 'company-sentiment-snapshot-negative-reviews',
    title: 'Negative Reviews',
    info: {
      text: 'An AI-generated summary of all negative reviews.',
    },
    plot: {
      defaultMetricOptions: ['negative_review'],
      primaryEntitySwitcher: true,
      chartType: 'sentiment_review',
    },
  },
];

const companySentimentOvertime: TabContent['items'] = [
  {
    position: { x: 0, width: 11, y: 0, height: 12 },
    id: 'company-sentiment-overtime-overall',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'line',
      defaultMetricOptions: companySentimentMetrics,
    },
  },
  {
    position: { x: 11, width: 11, y: 0, height: 12 },
    id: 'company-sentiment-overtime-business-outlook',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'line',
      defaultMetricOptions: companySentimentMetrics,
    },
  },
  {
    position: { x: 22, width: 11, y: 0, height: 12 },
    id: 'company-sentiment-overtime-career-growth',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'line',
      defaultMetricOptions: companySentimentMetrics,
    },
  },
  {
    position: { x: 0, width: 11, y: 12, height: 12 },
    id: 'company-sentiment-overtime-compensation',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'line',
      defaultMetricOptions: companySentimentMetrics,
    },
  },
  {
    position: { x: 11, width: 11, y: 12, height: 12 },
    id: 'company-sentiment-overtime-benefits',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'line',
      defaultMetricOptions: companySentimentMetrics,
    },
  },
  {
    position: { x: 22, width: 11, y: 12, height: 12 },
    id: 'company-sentiment-overtime-culture',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'line',
      defaultMetricOptions: companySentimentMetrics,
    },
  },
  {
    position: { x: 0, width: 11, y: 24, height: 12 },
    id: 'company-sentiment-overtime-work-life-balance',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'line',
      defaultMetricOptions: companySentimentMetrics,
    },
  },
  {
    position: { x: 11, width: 11, y: 24, height: 12 },
    id: 'company-sentiment-overtime-diversity-and-inclusion',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'line',
      defaultMetricOptions: companySentimentMetrics,
    },
  },
  {
    position: { x: 22, width: 11, y: 24, height: 12 },
    id: 'company-sentiment-overtime-leadership',
    title: '{{metric}}',
    info: {
      text: sentimentInfoText,
    },
    plot: {
      chartType: 'line',
      defaultMetricOptions: companySentimentMetrics,
    },
  },
  {
    position: { x: 33, width: 15, y: 0, height: 18 },
    id: 'company-sentiment-overtime-positive-reviews',
    title: 'Trending Positive Reviews',
    info: {
      text: 'An AI-generated summary of all positive reviews.',
    },
    plot: {
      defaultMetricOptions: ['positive_review'],
      primaryEntitySwitcher: true,
      chartType: 'sentiment_review',
    },
  },
  {
    position: { x: 33, width: 15, y: 18, height: 18 },
    id: 'company-sentiment-overtime-negative-reviews',
    title: 'Trending Negative Reviews',
    info: {
      text: 'An AI-generated summary of all negative reviews.',
    },
    plot: {
      defaultMetricOptions: ['negative_review'],
      primaryEntitySwitcher: true,
      chartType: 'sentiment_review',
    },
  },
];

const sharedConfig: Required<Pick<TabContent, 'primaryFilter' | 'grid'>> = {
  primaryFilter: {
    id: FilterId.COMPANY,
  },
  grid: { rows: DEFAULT_GRID_ROWS, columns: DEFAULT_GRID_COLUMNS },
};

const companySentimentSnapshotTab: TabContent = {
  id: 'company-sentiment-snapshot',
  items: companySentimentSingleSnapshotItems,
  ...sharedConfig,
  secondaryFilters: [
    FilterId.OVERALL_RATING,
    FilterId.ROLE,
    FilterId.SENIORITY,
    FilterId.GEOGRAPHY,
    FilterId.SNAPSHOT_DATE,
  ].map((id) => ({ id })),
};

const companySentimentOvertimeTab: TabContent = {
  id: 'company-sentiment-overtime',
  items: companySentimentOvertime,
  ...sharedConfig,
  primaryFilter: { ...sharedConfig.primaryFilter, tagColors: true },
  secondaryFilters: [
    FilterId.OVERALL_RATING,
    FilterId.ROLE,
    FilterId.SENIORITY,
    FilterId.GEOGRAPHY,
    FilterId.DATE_RANGE,
  ].map((id) => ({ id })),
};

export const companySentiment: Page = {
  type: PageType.REVELIO_PUBLIC,
  id: RevelioPageId.COMPANY_SENTIMENT,
  revelioPageId: RevelioPageId.COMPANY_SENTIMENT,
  name: 'Sentiment',
  owner: { id: 'revelio-labs', username: 'revelio-labs' },
  tabs: [
    {
      id: 'company-sentiment-snapshot',
      label: 'Snapshot',
      tabContent: companySentimentSnapshotTab,
      isActive: true,
    },
    {
      id: 'company-sentiment-overtime',
      label: 'Over Time',
      tabContent: companySentimentOvertimeTab,
      isActive: false,
    },
  ],
};

export const companySentimentDefaultMetric: Record<string, MetricValues> = {
  'company-sentiment-snapshot-overall': 'overall',
  'company-sentiment-snapshot-business-outlook': 'business_outlook',
  'company-sentiment-snapshot-career-growth': 'career_growth',
  'company-sentiment-snapshot-compensation': 'compensation',
  'company-sentiment-snapshot-benefits': 'benefits',
  'company-sentiment-snapshot-culture': 'culture',
  'company-sentiment-snapshot-work-life-balance': 'work_life_balance',
  'company-sentiment-snapshot-diversity-and-inclusion':
    'diversity_and_inclusion',
  'company-sentiment-snapshot-leadership': 'leadership',
  'company-sentiment-overtime-overall': 'overall',
  'company-sentiment-overtime-business-outlook': 'business_outlook',
  'company-sentiment-overtime-career-growth': 'career_growth',
  'company-sentiment-overtime-compensation': 'compensation',
  'company-sentiment-overtime-benefits': 'benefits',
  'company-sentiment-overtime-culture': 'culture',
  'company-sentiment-overtime-work-life-balance': 'work_life_balance',
  'company-sentiment-overtime-diversity-and-inclusion':
    'diversity_and_inclusion',
  'company-sentiment-overtime-leadership': 'leadership',
};
